import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUserRoles } from "@/hooks/useUserRoles";

interface ProductoCaducidad {
  id: string;
  producto_nombre: string;
  producto_codigo: string;
  fecha_caducidad: string;
  lote: string | null;
  dias_restantes: number;
}

interface NotificacionStockBajo {
  id: string;
  tipo: string;
  titulo: string;
  descripcion: string;
  created_at: string;
  leida: boolean;
}

interface LicenciaAlerta {
  id: string;
  empleado_nombre: string;
  empleado_puesto: string;
  fecha_vencimiento: string;
  dias_restantes: number;
  vencida: boolean;
}

interface AutorizacionOC {
  id: string;
  titulo: string;
  descripcion: string;
  created_at: string;
  orden_compra_id: string;
  folio?: string;
}

interface AutorizacionCotizacion {
  id: string;
  titulo: string;
  descripcion: string;
  created_at: string;
  cotizacion_id: string;
  folio?: string;
  cliente_nombre?: string;
}

interface ConfirmacionProveedor {
  id: string;
  orden_compra_id: string;
  folio: string;
  proveedor_nombre: string;
  confirmado_en: string;
}

interface NotificacionGeneral {
  id: string;
  tipo: string;
  titulo: string;
  descripcion: string;
  created_at: string;
}

export interface NotificacionesData {
  alertasCaducidad: ProductoCaducidad[];
  notificacionesStock: NotificacionStockBajo[];
  alertasLicencias: LicenciaAlerta[];
  autorizacionesOC: AutorizacionOC[];
  autorizacionesCotizacion: AutorizacionCotizacion[];
  confirmacionesProveedor: ConfirmacionProveedor[];
  notificacionesPrecios: NotificacionGeneral[];
  notificacionesPedidos: NotificacionGeneral[];
  notificacionesRechazo: NotificacionGeneral[];
  notificacionesCaducidadPush: NotificacionGeneral[];
  notificacionesFumigacionPush: NotificacionGeneral[];
  notificacionesPreciosVendedor: NotificacionGeneral[];
  notificacionesPreciosAdmin: NotificacionGeneral[];
  notificacionesProductoNuevo: NotificacionGeneral[];
  totalCount: number;
}

export const useNotificaciones = () => {
  const [notificaciones, setNotificaciones] = useState<NotificacionesData>({
    alertasCaducidad: [],
    notificacionesStock: [],
    alertasLicencias: [],
    autorizacionesOC: [],
    autorizacionesCotizacion: [],
    confirmacionesProveedor: [],
    notificacionesPrecios: [],
    notificacionesPedidos: [],
    notificacionesRechazo: [],
    notificacionesCaducidadPush: [],
    notificacionesFumigacionPush: [],
    notificacionesPreciosVendedor: [],
    notificacionesPreciosAdmin: [],
    notificacionesProductoNuevo: [],
    totalCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const { isAdmin, isSecretaria, isVendedor, isAlmacen, isGerenteAlmacen, hasAnyRole } = useUserRoles();

  const cargarNotificaciones = async () => {
    try {
      const [caducidad, stock, licencias, autorizaciones, autorizacionesCot, confirmaciones, precios, pedidos, rechazos, caducidadPush, fumigacionPush, preciosVendedor, preciosAdmin, productoNuevo] = await Promise.all([
        cargarAlertasCaducidad(),
        cargarNotificacionesStock(),
        cargarAlertasLicencias(),
        isAdmin ? cargarAutorizacionesOC() : Promise.resolve([]),
        isAdmin ? cargarAutorizacionesCotizacion() : Promise.resolve([]),
        cargarConfirmacionesProveedor(),
        isAdmin ? cargarNotificacionesPrecios() : Promise.resolve([]),
        cargarNotificacionesPedidos(),
        cargarNotificacionesRechazo(),
        cargarNotificacionesCaducidadPush(),
        cargarNotificacionesFumigacionPush(),
        cargarNotificacionesVendedorPrecios(),
        isAdmin ? cargarNotificacionesAdminPrecios() : Promise.resolve([]),
        cargarNotificacionesProductoNuevo(),
      ]);

      const total = caducidad.length + stock.length + licencias.length + autorizaciones.length + autorizacionesCot.length + confirmaciones.length + precios.length + pedidos.length + rechazos.length + caducidadPush.length + fumigacionPush.length + preciosVendedor.length + preciosAdmin.length + productoNuevo.length;
      setNotificaciones({
        alertasCaducidad: caducidad,
        notificacionesStock: stock,
        alertasLicencias: licencias,
        autorizacionesOC: autorizaciones,
        autorizacionesCotizacion: autorizacionesCot,
        confirmacionesProveedor: confirmaciones,
        notificacionesPrecios: precios,
        notificacionesPedidos: pedidos,
        notificacionesRechazo: rechazos,
        notificacionesCaducidadPush: caducidadPush,
        notificacionesFumigacionPush: fumigacionPush,
        notificacionesPreciosVendedor: preciosVendedor,
        notificacionesPreciosAdmin: preciosAdmin,
        notificacionesProductoNuevo: productoNuevo,
        totalCount: total,
      });
    } catch (error) {
      console.error("Error cargando notificaciones:", error);
    } finally {
      setLoading(false);
    }
  };

  // REMOVED: cargarConfirmacionesProveedor function - confirmation system deprecated
  const cargarConfirmacionesProveedor = async (): Promise<ConfirmacionProveedor[]> => {
    // Confirmation system was removed - always return empty array
    return [];
  };

  const cargarNotificacionesRechazo = async (): Promise<NotificacionGeneral[]> => {
    try {
      const isAdminOrSecretaria = isAdmin || isSecretaria;

      if (!isAdminOrSecretaria && !isVendedor) return [];

      // Admin/Secretaria: ver rechazos, cancelaciones y entregas parciales
      // Vendedor: ver pedidos rechazados + cancelados
      const tipos = isAdminOrSecretaria
        ? ["rechazo_entrega_total", "pedido_rechazado", "pedido_cancelado", "entrega_parcial"]
        : ["pedido_rechazado", "pedido_cancelado"];

      const { data, error } = await supabase
        .from("notificaciones")
        .select("id, tipo, titulo, descripcion, created_at")
        .in("tipo", tipos)
        .eq("leida", false)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) return [];
      return (data || []) as NotificacionGeneral[];
    } catch (error) {
      console.error("Error cargando notificaciones de rechazo:", error);
      return [];
    }
  };

  const cargarNotificacionesPrecios = async (): Promise<NotificacionGeneral[]> => {
    try {
      const { data, error } = await supabase
        .from("notificaciones")
        .select("id, tipo, titulo, descripcion, created_at")
        .in("tipo", ["revision_precio_requerida", "costo_incrementado"])
        .eq("leida", false)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) return [];
      return (data || []) as NotificacionGeneral[];
    } catch (error) {
      console.error("Error cargando notificaciones de precios:", error);
      return [];
    }
  };

  const cargarNotificacionesPedidos = async (): Promise<NotificacionGeneral[]> => {
    try {
      // Only load for admin and secretaria
      const { data: { user } } = await supabase.auth.getUser();
      if (!isAdmin && !isSecretaria) return [];

      const { data, error } = await supabase
        .from("notificaciones")
        .select("id, tipo, titulo, descripcion, created_at")
        .eq("tipo", "nuevo_pedido_vendedor")
        .eq("leida", false)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) return [];
      return (data || []) as NotificacionGeneral[];
    } catch (error) {
      console.error("Error cargando notificaciones de pedidos:", error);
      return [];
    }
  };

  const cargarAlertasCaducidad = async (): Promise<ProductoCaducidad[]> => {
    try {
      const { data: productos, error: productosError } = await supabase
        .from("productos")
        .select("id, nombre, codigo, stock_actual")
        .eq("maneja_caducidad", true)
        .gt("stock_actual", 0);

      if (productosError || !productos || productos.length === 0) {
        return [];
      }

      const productosIds = productos.map(p => p.id);
      const fechaActual = new Date();
      const fecha30Dias = new Date();
      fecha30Dias.setDate(fecha30Dias.getDate() + 30);

      const { data: movimientos, error: movimientosError } = await supabase
        .from("inventario_movimientos")
        .select("producto_id, fecha_caducidad, lote")
        .in("producto_id", productosIds)
        .eq("tipo_movimiento", "entrada")
        .not("fecha_caducidad", "is", null)
        .lte("fecha_caducidad", fecha30Dias.toISOString().split("T")[0])
        .gte("fecha_caducidad", fechaActual.toISOString().split("T")[0])
        .order("fecha_caducidad", { ascending: true });

      if (movimientosError) return [];

      const alertasFormateadas: ProductoCaducidad[] = [];
      const lotesUnicos = new Set<string>();

      movimientos?.forEach(mov => {
        const producto = productos.find(p => p.id === mov.producto_id);
        if (!producto) return;

        const loteKey = `${mov.producto_id}-${mov.lote || "sin-lote"}-${mov.fecha_caducidad}`;
        if (lotesUnicos.has(loteKey)) return;
        lotesUnicos.add(loteKey);

        const fechaCad = new Date(mov.fecha_caducidad!);
        const diasRestantes = Math.ceil((fechaCad.getTime() - fechaActual.getTime()) / (1000 * 60 * 60 * 24));

        alertasFormateadas.push({
          id: loteKey,
          producto_nombre: producto.nombre,
          producto_codigo: producto.codigo,
          fecha_caducidad: mov.fecha_caducidad!,
          lote: mov.lote,
          dias_restantes: diasRestantes,
        });
      });

      return alertasFormateadas;
    } catch (error) {
      console.error("Error cargando alertas de caducidad:", error);
      return [];
    }
  };

  const cargarNotificacionesStock = async (): Promise<NotificacionStockBajo[]> => {
    try {
      const { data, error } = await supabase
        .from("notificaciones")
        .select("*")
        .eq("tipo", "stock_bajo")
        .eq("leida", false)
        .order("created_at", { ascending: false });

      if (error) return [];
      return data || [];
    } catch (error) {
      console.error("Error cargando notificaciones de stock:", error);
      return [];
    }
  };

  const cargarAutorizacionesOC = async (): Promise<AutorizacionOC[]> => {
    try {
      const { data, error } = await supabase
        .from("notificaciones")
        .select(`
          id,
          titulo,
          descripcion,
          created_at,
          orden_compra_id
        `)
        .eq("tipo", "autorizacion_oc")
        .eq("leida", false)
        .not("orden_compra_id", "is", null)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching autorizaciones:", error);
        return [];
      }
      
      // Fetch folio for each OC - show all pending regardless of OC status
      const autorizaciones: AutorizacionOC[] = [];
      for (const notif of data || []) {
        if (notif.orden_compra_id) {
          const { data: oc } = await supabase
            .from("ordenes_compra")
            .select("folio, status")
            .eq("id", notif.orden_compra_id)
            .maybeSingle();
          
          // Show notification if OC exists and is not already authorized/rejected
          if (oc && !["autorizada", "enviada", "rechazada", "recibida"].includes(oc.status)) {
            autorizaciones.push({
              ...notif,
              orden_compra_id: notif.orden_compra_id,
              folio: oc.folio,
            });
          }
        }
      }
      
      return autorizaciones;
    } catch (error) {
      console.error("Error cargando autorizaciones de OC:", error);
      return [];
    }
  };

  const cargarAutorizacionesCotizacion = async (): Promise<AutorizacionCotizacion[]> => {
    try {
      const { data, error } = await supabase
        .from("notificaciones")
        .select(`
          id,
          titulo,
          descripcion,
          created_at,
          cotizacion_id
        `)
        .eq("tipo", "autorizacion_cotizacion")
        .eq("leida", false)
        .not("cotizacion_id", "is", null)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching autorizaciones cotizacion:", error);
        return [];
      }
      
      const autorizaciones: AutorizacionCotizacion[] = [];
      for (const notif of data || []) {
        if (notif.cotizacion_id) {
          const { data: cot } = await supabase
            .from("cotizaciones")
            .select(`
              folio,
              status,
              cliente:clientes(nombre)
            `)
            .eq("id", notif.cotizacion_id)
            .maybeSingle();
          
          // Show notification if cotizacion exists and is pending authorization
          if (cot && cot.status === "pendiente_autorizacion") {
            autorizaciones.push({
              ...notif,
              cotizacion_id: notif.cotizacion_id,
              folio: cot.folio,
              cliente_nombre: (cot.cliente as any)?.nombre || "Cliente",
            });
          }
        }
      }
      
      return autorizaciones;
    } catch (error) {
      console.error("Error cargando autorizaciones de cotizacion:", error);
      return [];
    }
  };

  const cargarAlertasLicencias = async (): Promise<LicenciaAlerta[]> => {
    try {
      const fechaActual = new Date();
      const fecha30Dias = new Date();
      fecha30Dias.setDate(fecha30Dias.getDate() + 30);

      // Obtener empleados que son choferes o vendedores
      const { data: empleados, error: empleadosError } = await supabase
        .from("empleados")
        .select("id, nombre_completo, puesto")
        .eq("activo", true)
        .in("puesto", ["Chofer", "Vendedor"]);

      if (empleadosError || !empleados || empleados.length === 0) {
        return [];
      }

      const empleadosIds = empleados.map(e => e.id);

      // Obtener documentos de licencias
      const { data: documentos, error: documentosError } = await supabase
        .from("empleados_documentos")
        .select("empleado_id, fecha_vencimiento")
        .in("empleado_id", empleadosIds)
        .eq("tipo_documento", "licencia_conducir")
        .not("fecha_vencimiento", "is", null);

      if (documentosError || !documentos) return [];

      const alertas: LicenciaAlerta[] = [];

      documentos.forEach(doc => {
        const empleado = empleados.find(e => e.id === doc.empleado_id);
        if (!empleado || !doc.fecha_vencimiento) return;

        const fechaVencimiento = new Date(doc.fecha_vencimiento);
        
        // Excluir licencias permanentes (año 2099)
        if (fechaVencimiento.getFullYear() === 2099) return;

        const diasRestantes = Math.ceil((fechaVencimiento.getTime() - fechaActual.getTime()) / (1000 * 60 * 60 * 24));
        
        // Mostrar si está vencida o vence en los próximos 30 días
        if (diasRestantes <= 30) {
          alertas.push({
            id: `${doc.empleado_id}-licencia`,
            empleado_nombre: empleado.nombre_completo,
            empleado_puesto: empleado.puesto,
            fecha_vencimiento: doc.fecha_vencimiento,
            dias_restantes: diasRestantes,
            vencida: diasRestantes < 0,
          });
        }
      });

      // Ordenar: vencidas primero, luego por días restantes
      return alertas.sort((a, b) => {
        if (a.vencida && !b.vencida) return -1;
        if (!a.vencida && b.vencida) return 1;
        return a.dias_restantes - b.dias_restantes;
      });
    } catch (error) {
      console.error("Error cargando alertas de licencias:", error);
      return [];
    }
  };

  const cargarNotificacionesCaducidadPush = async (): Promise<NotificacionGeneral[]> => {
    try {
      if (!isAdmin && !isAlmacen && !isGerenteAlmacen) return [];

      const { data, error } = await supabase
        .from("notificaciones")
        .select("id, tipo, titulo, descripcion, created_at")
        .in("tipo", ["caducidad_critica", "caducidad_vencida"])
        .eq("leida", false)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) return [];
      return (data || []) as NotificacionGeneral[];
    } catch { return []; }
  };

  const cargarNotificacionesFumigacionPush = async (): Promise<NotificacionGeneral[]> => {
    try {
      if (!isAdmin && !isAlmacen && !isGerenteAlmacen) return [];

      const { data, error } = await supabase
        .from("notificaciones")
        .select("id, tipo, titulo, descripcion, created_at")
        .in("tipo", ["fumigacion_proxima", "fumigacion_vencida", "fumigacion_sin_fecha"])
        .eq("leida", false)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) return [];
      return (data || []) as NotificacionGeneral[];
    } catch { return []; }
  };

  const cargarNotificacionesVendedorPrecios = async (): Promise<NotificacionGeneral[]> => {
    try {
      if (!isAdmin && !isSecretaria && !isVendedor) return [];

      const { data, error } = await supabase
        .from("notificaciones")
        .select("id, tipo, titulo, descripcion, created_at")
        .eq("tipo", "precio_actualizado")
        .eq("leida", false)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) return [];
      return (data || []) as NotificacionGeneral[];
    } catch { return []; }
  };

  const cargarNotificacionesAdminPrecios = async (): Promise<NotificacionGeneral[]> => {
    try {
      if (!isAdmin) return [];

      const { data, error } = await supabase
        .from("notificaciones")
        .select("id, tipo, titulo, descripcion, created_at")
        .eq("tipo", "precio_modificado_admin")
        .eq("leida", false)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) return [];
      return (data || []) as NotificacionGeneral[];
    } catch { return []; }
  };

  const cargarNotificacionesProductoNuevo = async (): Promise<NotificacionGeneral[]> => {
    try {
      if (!isAdmin && !isSecretaria && !isVendedor) return [];

      const { data, error } = await supabase
        .from("notificaciones")
        .select("id, tipo, titulo, descripcion, created_at")
        .eq("tipo", "producto_nuevo")
        .eq("leida", false)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) return [];
      return (data || []) as NotificacionGeneral[];
    } catch { return []; }
  };

  useEffect(() => {
    cargarNotificaciones();

    // Recargar cada 2 minutos
    const interval = setInterval(cargarNotificaciones, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const marcarComoLeida = async (notificacionId: string) => {
    try {
      const { error } = await supabase
        .from("notificaciones")
        .update({ leida: true })
        .eq("id", notificacionId);

      if (error) throw error;
      
      // Recargar notificaciones
      await cargarNotificaciones();
    } catch (error) {
      console.error("Error marcando notificación como leída:", error);
    }
  };

  const marcarTodasComoLeidas = async () => {
    try {
      const { error } = await supabase
        .from("notificaciones")
        .update({ leida: true })
        .eq("leida", false);
      if (error) throw error;
      await cargarNotificaciones();
    } catch (error) {
      console.error("Error marcando todas como leídas:", error);
    }
  };

  return {
    ...notificaciones,
    loading,
    isAdmin,
    marcarComoLeida,
    marcarTodasComoLeidas,
    recargar: cargarNotificaciones,
  };
};
